import { useEffect, useMemo, useState } from 'react';
import { BigNumber } from 'bignumber.js';
import PropTypes from 'prop-types';

import './SamplesList.css';

import { useMtg } from '../../hook/useMtg';
import { cardFinish, cardRarity } from '../../hoc/MtgProvider';
import { simpleSelectTypeCls, SimpleSelectLabeled } from '../SimpleComponents/SimpleSelect';
import OrderingSwitch, { orderType } from '../SimpleComponents/OrderingSwitch';
import SimpleCheckbox, { CheckboxTypes } from '../SimpleComponents/SimpleCheckbox';
import SamplesListContent, { sortType } from './SampleListContent';
import { buildClass } from '../../libs/classes';

export const viewModeList = {
	rows: {
		value: 'rows',
		description: 'Show all cards as distinct rows',
		showAsPacks: false,
		showAsTiles: false,
	},
	tiles: {
		value: 'tiles',
		description: 'Show cards as thumbnails',
		showAsPacks: false,
		showAsTiles: true,
	},
	combined: {
		value: 'combined',
		description: 'Show all cards as rows and combine cards with the same collector number into packs',
		showAsPacks: true,
		showAsTiles: false,
	},
};

/**
 * List of card instances (samples) with controls
 * @component
 */
const SamplesList = ({
	samples,
	isOwned,
	selectedIds = new Set(),
	viewMode = viewModeList.combined,
	showStorage = true,
	show_checkbox = false,
	show_change_mode = true,
	show_subhead = true,
	default_sort = sortType.collectorNumber,
	onActivate,
	onSelect,
}) => {
	const [sorting, setSorting] = useState(default_sort);
	const [ordering, setOrdering] = useState(orderType.ascending);
	const [selectedViewMode, setSelectedViewMode] = useState(viewMode);
	const [stat, setStat] = useState({});
	const [value, setValue] = useState(BigNumber('0'));
	const [valueTotal, setValueTotal] = useState(BigNumber('0'));
	const { cutoffPrice } = useMtg();

	useEffect(() => {
		console.log('Initialization: SamplesList');
	}, []);

	useEffect(() => {
		setSorting(default_sort);
	}, [default_sort]);

	useEffect(() => {
		setSelectedViewMode(viewMode);
	}, [viewMode]);

	useEffect(() => {
		const statUpd = {
			total: 0,
			c: 0,
			uc: 0,
			r: 0,
			m: 0,
			f: 0,
			e: 0,
		};
		for (let key of Object.keys(samples)) {
			statUpd.total += samples[key].qty;
			statUpd.c += samples[key]?.card?.rarity === cardRarity.common ? samples[key].qty : 0;
			statUpd.uc += samples[key]?.card?.rarity === cardRarity.uncommon ? samples[key].qty : 0;
			statUpd.r += samples[key]?.card?.rarity === cardRarity.rare ? samples[key].qty : 0;
			statUpd.m += samples[key]?.card?.rarity === cardRarity.mythic ? samples[key].qty : 0;
			statUpd.f += samples[key].finish === cardFinish.foil ? samples[key].qty : 0;
			statUpd.e += samples[key].finish === cardFinish.etched ? samples[key].qty : 0;
		}
		setStat(statUpd);
	}, [samples]);

	useEffect(() => {
		let valueUpd = BigNumber('0');
		let valueTotalUpd = BigNumber('0');
		for (let key of Object.keys(samples)) {
			const sample = samples[key];
			if (sample?.card) {
				let price = BigNumber('0');
				if (sample.finish === cardFinish.nonfoil) {
					price = BigNumber(sample.card.price_usd);
				} else if (sample.finish === cardFinish.foil) {
					price = BigNumber(sample.card.price_usd_foil);
				} else if (sample.finish === cardFinish.etched) {
					price = BigNumber(sample.card.price_usd_etched);
				}
				if (price.gte(cutoffPrice)) {
					valueUpd = valueUpd.plus(price.times(sample.qty));
				}
				valueTotalUpd = valueTotalUpd.plus(price.times(sample.qty));
			}
		}
		setValue(valueUpd);
		setValueTotal(valueTotalUpd);
	}, [samples, cutoffPrice]);

	const totalCardsElement = useMemo(
		() => (
			<span className="value">
				<span className="total" title="Total cards">
					{stat.total}&thinsp;:&thinsp;
				</span>
				<span className="detailed">
					<span className="common" title="Common cards">
						{stat.c}&thinsp;
					</span>
					/
					<span className="uncommon" title="Uncommon cards">
						&thinsp;{stat.uc}&thinsp;
					</span>
					/
					<span className="rare" title="Rare cards">
						&thinsp;{stat.r}&thinsp;
					</span>
					/
					<span className="mythic" title="Mythic cards">
						&thinsp;{stat.m}&thinsp;
					</span>
					{stat.f > 0 || stat.e > 0 ? <>&nbsp;[</> : null}
					{stat.f > 0 ? (
						<span className="foil" title="Foil cards">
							{stat.f}&thinsp;
						</span>
					) : null}
					{stat.e > 0 ? (
						<>
							/
							<span className="etched" title="Etched cards">
								&thinsp;{stat.e}
							</span>
						</>
					) : null}
					{stat.f > 0 || stat.e > 0 ? <>]</> : null}
				</span>
			</span>
		),
		[stat]
	);

	function getSelectAllState() {
		const sampleCount = Object.keys(samples).length;
		if (sampleCount === 0) return false;
		return selectedIds.size >= sampleCount;
	}

	return (
		<div className="sample-list">
			{show_subhead ? (
				<div className="subhead">
					{show_checkbox ? (
						<SimpleCheckbox
							className="select-all"
							state={getSelectAllState()}
							title="Select or Deselect all"
							type={CheckboxTypes.TICK}
							onClick={handleSelectAll}
						/>
					) : null}
					{show_change_mode ? (
						<div
							className={buildClass('view-mode', selectedViewMode.value)}
							title={selectedViewMode.description}
							onClick={handleSwitchViewMode}
						/>
					) : null}
					<div className="total_cards">
						<span className="label"># Cards:</span>&thinsp;{totalCardsElement}
					</div>
					<div className="total_value">
						<span className="label">Total value:</span>&nbsp;
						<span className="clipped" title={`Total value of cards with price >= $${cutoffPrice}`}>
							<span className="usd">$&#8202;</span>
							<span className="value">{value.toString()}</span>
						</span>
						&nbsp;
						<span className="full" title="Total value of all cards">
							<span className="usd">$&#8202;</span>
							<span className="value">{valueTotal.toString()}</span>
						</span>
					</div>
					<div className="spacer" />
					<div className="sorting">
						<SimpleSelectLabeled
							className={simpleSelectTypeCls.thin}
							label="Sort:"
							value={sorting}
							onChange={handleSortChange}
						>
							<option value={sortType.price}>Value</option>
							<option value={sortType.rarity}>Rarity</option>
							<option value={sortType.collectorNumber}>Collector Number</option>
							<option value={sortType.dateChanged}>Date changed</option>
							<option value={sortType.name}>Card Name</option>
						</SimpleSelectLabeled>
						<OrderingSwitch state={ordering} onClick={handleSwitchOrdering} />
					</div>
				</div>
			) : null}
			<SamplesListContent
				isOwned={isOwned}
				ordering={ordering}
				samples={samples}
				selectedIds={selectedIds}
				show_as_packs={selectedViewMode.showAsPacks}
				show_as_tiles={selectedViewMode.showAsTiles}
				show_checkbox={show_checkbox}
				showStorage={showStorage}
				sorting={sorting}
				onActivate={onActivate}
				onSelect={onSelect}
			/>
		</div>
	);

	function handleSelectAll() {
		//console.log('handleClickSelectAll');
		const selected_all = getSelectAllState();
		const selected_none = selectedIds.size > 0 ? false : true;
		// Deselect
		if (selected_all || !selected_none) {
			onSelect?.(null, false, true);
			return;
		}
		// Select all
		if (selected_none) {
			onSelect?.(null, true, false);
			return;
		}
	}

	function handleSortChange(event) {
		setSorting(event.target.value);
	}

	// Switch sorting ordering
	function handleSwitchOrdering() {
		const orderingUpd = ordering === orderType.ascending ? orderType.descending : orderType.ascending;
		setOrdering(orderingUpd);
	}

	// Switch view mode
	function handleSwitchViewMode() {
		switch (selectedViewMode.value) {
			case viewModeList.combined.value:
				setSelectedViewMode(viewModeList.rows);
				break;
			case viewModeList.rows.value:
				setSelectedViewMode(viewModeList.tiles);
				break;
			case viewModeList.tiles.value:
			default:
				setSelectedViewMode(viewModeList.combined);
		}
	}
};

SamplesList.propTypes = {
	// Associative array (Object) of samples
	samples: PropTypes.objectOf(PropTypes.object).isRequired,
	// Is this storage owned by current user
	isOwned: PropTypes.bool.isRequired,
	// Set of selected samples IDs
	selectedIds: PropTypes.instanceOf(Set),
	// View mode for the list
	viewMode: PropTypes.shape({
		value: PropTypes.string,
		description: PropTypes.string,
		showAsPacks: PropTypes.bool,
		showAsTiles: PropTypes.bool,
	}),
	// Flag that storage should be displayed for each sample
	showStorage: PropTypes.bool,
	// Flag that checkbox should be displayed for each sample
	show_checkbox: PropTypes.bool,
	// Flag that change mode element should be showed
	show_change_mode: PropTypes.bool,
	// Show subhead
	show_subhead: PropTypes.bool,
	// Default sorting
	default_sort: PropTypes.string,
	// Callback on activate (select as active) sample
	onActivate: PropTypes.func.isRequired,
	// Callback on select (with checkbox) sample
	onSelect: PropTypes.func,
};

export default SamplesList;
